import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useUserPermissions } from "@/api/hooks/permissions";
import { TAB_REGISTRY } from "@/registry/appRegistry";
import type { TabId } from "@/api/types";

interface TabGuardProps {
  tabId: TabId;
  children: ReactNode;
}

/**
 * Renders its children only when the current user is permitted to
 * see ``tabId``. While the permissions query is loading, shows a
 * spinner. On deny, redirects to the user's default tab (the first
 * tab in ``TAB_REGISTRY`` order they have access to). With zero
 * permissions, redirects to ``/no-access``.
 */
export default function TabGuard({ tabId, children }: TabGuardProps) {
  const { data, isLoading, isError } = useUserPermissions();

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return <Navigate to="/auth-error" replace />;
  }

  const tabs = data?.tabs ?? [];
  if (tabs.includes(tabId)) {
    return <>{children}</>;
  }

  const defaultTab = TAB_REGISTRY.find((t) => tabs.includes(t.id));
  if (!defaultTab) {
    return <Navigate to="/no-access" replace />;
  }
  return <Navigate to={defaultTab.basePath} replace />;
}
